import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers } from '../../../store/slices/userSlice';
import PresignedAvatar from '../../../components/common/PresignedAvatar';
import { fetchNeedToCall, saveNeedToCall } from '../services/noteService';
import NeedToCallDetailModal from './NeedToCallDetailModal';

const newItemId = () => `call-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const userDisplayName = (user) => {
  if (!user) return '';
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.username || 'Someone';
};

const userPhone = (user) =>
  String(user?.mobileNumber || user?.contactNumber || '').trim();

const initialsFromName = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0] || '?').slice(0, 2).toUpperCase();
};

const NeedToCallList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const users = useSelector((state) => state.users.users || []);
  const saveTimerRef = useRef(null);
  const itemsRef = useRef([]);
  const dirtyRef = useRef(false);

  const [items, setItems] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [pickedUserId, setPickedUserId] = useState('');
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [error, setError] = useState('');

  itemsRef.current = items;

  useEffect(() => {
    dispatch(fetchUsers());
    let cancelled = false;
    fetchNeedToCall()
      .then((list) => {
        if (!cancelled) setItems(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Could not load call list');
        }
      });
    const refresh = (event) => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      dirtyRef.current = false;
      const immediate = event?.detail?.items;
      if (Array.isArray(immediate)) {
        if (saveTimerRef.current) {
          window.clearTimeout(saveTimerRef.current);
          saveTimerRef.current = null;
        }
        dirtyRef.current = false;
        setItems(immediate);
        return;
      }
      fetchNeedToCall()
        .then((list) => {
          if (cancelled) return;
          if (saveTimerRef.current) {
            window.clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
          }
          dirtyRef.current = false;
          setItems(Array.isArray(list) ? list : []);
        })
        .catch(() => {});
    };
    window.addEventListener('workloop:need-to-call-changed', refresh);
    return () => {
      cancelled = true;
      window.removeEventListener('workloop:need-to-call-changed', refresh);
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
      if (dirtyRef.current) {
        saveNeedToCall(itemsRef.current).catch(() => {});
      }
    };
  }, [dispatch]);

  const persist = (next) => {
    dirtyRef.current = true;
    setItems(next);
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = window.setTimeout(() => {
      saveNeedToCall(next).catch((err) => {
        setError(err?.response?.data?.error || err?.message || 'Could not save call list');
      });
    }, 400);
  };

  const query = name.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (query.length < 1) return [];
    return users
      .filter((user) => user.id !== currentUser?.id)
      .filter((user) => {
        const label = userDisplayName(user).toLowerCase();
        const username = String(user.username || '').toLowerCase();
        return label.includes(query) || username.includes(query);
      })
      .slice(0, 8);
  }, [users, currentUser?.id, query]);

  const pickUser = (user) => {
    setPickedUserId(user.id);
    setName(userDisplayName(user));
    setPhone(userPhone(user));
    setSuggestionsOpen(false);
  };

  const resetForm = () => {
    setName('');
    setPhone('');
    setReason('');
    setPickedUserId('');
    setSuggestionsOpen(false);
  };

  const openAddModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const closeAddModal = () => {
    resetForm();
    setModalOpen(false);
  };

  const addPerson = () => {
    const nextName = name.trim();
    if (!nextName) return;
    const duplicate = items.some((item) => (
      String(item.name || '').trim().toLowerCase() === nextName.toLowerCase()
      && !item.done
    ));
    if (duplicate) {
      closeAddModal();
      return;
    }
    persist([
      {
        id: newItemId(),
        targetUserId: pickedUserId || null,
        name: nextName,
        phone: phone.trim(),
        reason: reason.trim(),
        done: false,
        status: 'open',
      },
      ...items,
    ]);
    closeAddModal();
  };

  const isClosed = (item) => Boolean(item.done) || item.status === 'skipped' || item.status === 'called';
  const pending = items.filter((item) => !isClosed(item));
  const closed = items.filter((item) => isClosed(item));

  const patchItem = (itemId, patch) => {
    persist(items.map((entry) => (entry.id === itemId ? { ...entry, ...patch } : entry)));
  };

  const closeDetail = () => setDetailItem(null);

  return (
    <section className="h-full min-h-0 flex flex-col bg-white dark:bg-gray-900">
      <div className="flex-shrink-0 flex items-start justify-between gap-2 px-3.5 pt-3.5 pb-2.5 border-b border-gray-100 dark:border-gray-800">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Need to call</h2>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            {pending.length
              ? `${pending.length} waiting`
              : 'Add who you still need to call'}
          </p>
        </div>
        <button
          type="button"
          onClick={openAddModal}
          className="flex-shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-lg bg-sky-600 text-white text-lg leading-none hover:bg-sky-700"
          title="Add someone to call"
          aria-label="Add someone to call"
        >
          +
        </button>
      </div>
      {error ? (
        <p className="px-3 pt-2 text-[11px] text-rose-600 dark:text-rose-400">{error}</p>
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2 space-y-1">
        {items.length === 0 ? (
          <p className="text-[11px] text-gray-400 px-0.5">Nobody on the list yet.</p>
        ) : (
          [...pending, ...closed].map((item) => {
            const user = users.find((entry) => String(entry.id) === String(item.targetUserId));
            const label = item.name;
            const closedRow = isClosed(item);
            const statusLabel = item.status === 'skipped'
              ? 'Skipped'
              : item.status === 'followup'
                ? 'Follow-up'
                : closedRow
                  ? 'Called'
                  : '';
            return (
              <button
                type="button"
                key={item.id}
                onClick={() => setDetailItem(item)}
                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left ${
                  closedRow
                    ? 'bg-white/40 dark:bg-gray-900/30 opacity-60'
                    : 'bg-white dark:bg-gray-900/70 hover:bg-sky-50 dark:hover:bg-sky-950/40'
                }`}
              >
                {user ? (
                  <PresignedAvatar
                    fileKey={user.profilePicture}
                    cacheKey={user.username}
                    name={label}
                    initials={initialsFromName(label)}
                    className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                    fallbackClassName="flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-white text-[10px] font-bold flex-shrink-0"
                  />
                ) : (
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-white text-[10px] font-bold flex-shrink-0">
                    {initialsFromName(label)}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <p className={`text-xs font-medium text-gray-900 dark:text-white truncate ${closedRow ? 'line-through' : ''}`}>
                    {label}
                    {item.phone ? (
                      <span className="ml-1.5 font-normal text-gray-500">{item.phone}</span>
                    ) : null}
                  </p>
                  {item.reason ? (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate">{item.reason}</p>
                  ) : null}
                </div>
                {statusLabel ? (
                  <span className="flex-shrink-0 text-[10px] font-semibold text-gray-500">
                    {statusLabel}
                  </span>
                ) : null}
              </button>
            );
          })
        )}
      </div>
      {modalOpen && typeof document !== 'undefined' ? ReactDOM.createPortal(
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-label="Add someone to call"
          onClick={closeAddModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Add to call list</h3>
            <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
              Type a name or pick a teammate. Phone and reason of call are optional.
            </p>
            <div className="relative mt-4">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                Name
                <input
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setPickedUserId('');
                    setSuggestionsOpen(true);
                  }}
                  onFocus={() => setSuggestionsOpen(true)}
                  onBlur={() => window.setTimeout(() => setSuggestionsOpen(false), 120)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addPerson();
                    }
                    if (event.key === 'Escape') setSuggestionsOpen(false);
                  }}
                  placeholder="Name or teammate"
                  className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  autoFocus
                />
              </label>
              {suggestionsOpen && suggestions.length > 0 ? (
                <ul className="absolute left-0 right-0 z-20 mt-1 max-h-44 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg">
                  {suggestions.map((user) => {
                    const label = userDisplayName(user);
                    return (
                      <li key={user.id}>
                        <button
                          type="button"
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => pickUser(user)}
                          className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left hover:bg-sky-50 dark:hover:bg-sky-950/40"
                        >
                          <PresignedAvatar
                            fileKey={user.profilePicture}
                            cacheKey={user.username}
                            name={label}
                            initials={initialsFromName(label)}
                            className="h-6 w-6 rounded-full object-cover flex-shrink-0"
                            fallbackClassName="flex h-6 w-6 items-center justify-center rounded-full bg-sky-600 text-white text-[10px] font-bold flex-shrink-0"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs font-medium text-gray-900 dark:text-white">
                              {label}
                            </span>
                            {user.username ? (
                              <span className="block truncate text-[10px] text-gray-400">@{user.username}</span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </div>
            <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Phone
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addPerson();
                  }
                }}
                placeholder="Optional"
                className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </label>
            <label className="mt-3 block text-xs font-medium text-gray-600 dark:text-gray-300">
              Reason
              <input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addPerson();
                  }
                }}
                placeholder="Optional"
                className="mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeAddModal}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!name.trim()}
                onClick={addPerson}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}
      {detailItem ? (
        <NeedToCallDetailModal
          item={detailItem}
          user={users.find((entry) => String(entry.id) === String(detailItem.targetUserId))}
          onClose={closeDetail}
          onCalled={() => {
            patchItem(detailItem.id, { done: true, status: 'called' });
            closeDetail();
          }}
          onSkip={() => {
            patchItem(detailItem.id, { done: true, status: 'skipped' });
            closeDetail();
          }}
          onFollowUp={() => {
            patchItem(detailItem.id, { done: false, status: 'followup' });
            closeDetail();
          }}
        />
      ) : null}
    </section>
  );
};

export default NeedToCallList;
